/**
 * DocumentDetailPage — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/05-flows.md § 11.2
 *
 * The "View" affordance on a filled wallet slot lands here. Shows:
 *   - source PDF/image (placeholder thumbnail for now)
 *   - expiry status banner (red / amber / muted by days-remaining)
 *   - read-only extracted fields (tap to edit inline — future)
 *   - actions: Replace (re-upload + supersede) / Delete (soft + 30-day grace)
 *
 * Edit inline on individual fields is a follow-up. Replace + Delete ship today.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { Button, Card, StatusPill } from '@/components/ui';
import { Icon } from '@/components/kol/Icon';
import { Plate, LTR, Brand } from '@/components/Bidi';
import { ApiError, deleteDocument, getDocument, getDocumentDownloadUrl, type DocumentDetail } from '@/lib/api';

const DOC_TYPE_TITLES: Record<string, { he: string; en: string }> = {
  VEHICLE_REGISTRATION: { he: 'רישיון רכב', en: 'Vehicle license' },
  INSURANCE_COMPULSORY: { he: 'ביטוח חובה', en: 'Compulsory insurance' },
  INSURANCE_COMPREHENSIVE: { he: 'ביטוח מקיף', en: 'Comprehensive insurance' },
  SERVICE_CONTRACT: { he: 'כתבי שרות', en: 'Service contract' },
  INSURANCE_POLICY: { he: 'פוליסת ביטוח', en: 'Insurance policy' },
  DRIVER_LICENSE: { he: 'רישיון נהיגה', en: 'Driver license' },
  OTHER: { he: 'מסמך אחר', en: 'Other document' },
};

export function DocumentDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  // Arrived from a קוֹל surface → back/close/replace return into /kol/*.
  const kol = params.get('kol') === '1';
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const d = await getDocument(id);
        if (cancelled) return;
        setDoc(d);
        // Fetch a presigned download URL in parallel; reused by the inline
        // preview <img>/<iframe> and the Download button.
        try {
          const dl = await getDocumentDownloadUrl(id);
          if (!cancelled) {
            // Storage-stub URL means the API isn't actually serving real GCS
            // (local-dev only); skip the preview to avoid a broken thumbnail.
            if (!dl.url.includes('storage-stub.local')) setPreviewUrl(dl.url);
            setFilename(dl.filename);
          }
        } catch {
          // non-fatal — fall back to the icon placeholder
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  async function onDownload() {
    if (!doc) return;
    try {
      const dl = await getDocumentDownloadUrl(doc.id);
      // Trigger a real download (not just a tab open) by setting `download`.
      // Browsers honor it for same-origin OR when the response Content-Disposition
      // header is `attachment`. GCS V4 signed URLs are cross-origin so the browser
      // may open the file in a new tab on iOS Safari instead — fine fallback.
      const a = document.createElement('a');
      a.href = dl.url;
      a.download = dl.filename;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    }
  }

  async function onDelete() {
    if (!doc) return;
    setError(null);
    setPending(true);
    try {
      await deleteDocument(doc.id);
      const backTo = kol
        ? doc.vehicleId
          ? `/kol/vehicles/${doc.vehicleId}/documents`
          : '/kol'
        : doc.vehicleId
          ? `/vehicles/${doc.vehicleId}`
          : '/';
      navigate(backTo, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setPending(false);
    }
  }

  const title = useMemo(() => {
    if (!doc) return t('document.title', { defaultValue: 'מסמך' });
    const lang = (i18n.language?.startsWith('he') ? 'he' : 'en') as 'he' | 'en';
    return DOC_TYPE_TITLES[doc.documentType]?.[lang] ?? doc.documentType;
  }, [doc, i18n.language, t]);

  return (
    <Layout
      title={title}
      trailing={
        <Link
          to={
            kol
              ? doc?.vehicleId
                ? `/kol/vehicles/${doc.vehicleId}/documents`
                : '/kol'
              : doc?.vehicleId
                ? `/vehicles/${doc.vehicleId}`
                : '/'
          }
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="arrow" size={19} />
        </Link>
      }
    >
      {error && (
        <p role="alert" className="text-body-sm text-carnaf-red mb-4">
          {error}
        </p>
      )}
      {!doc && !error && <p className="text-carnaf-ink-muted">{t('common.loading')}</p>}

      {doc && (
        <div className="space-y-5 pb-32">
          {/* Source preview — actual file when we have a presigned URL,
              icon placeholder otherwise. Image MIME types render as <img>;
              PDFs render as an iframe (works on desktop + Android; iOS
              Safari shows the first page as a fallback). */}
          <Card variant="nested" padded={false}>
            {previewUrl && doc.fileMimeType.startsWith('image/') ? (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('document.open_source', { defaultValue: 'פתח את המקור' })}
              >
                <img
                  src={previewUrl}
                  alt={filename || doc.documentType}
                  className="w-full aspect-[3/4] object-cover rounded-md"
                />
              </a>
            ) : previewUrl && doc.fileMimeType === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                title={filename || doc.documentType}
                className="w-full aspect-[3/4] rounded-md border-0 bg-carnaf-paper-elevated"
              />
            ) : (
              <div className="aspect-[3/4] flex flex-col items-center justify-center text-carnaf-ink-faint bg-gradient-to-br from-carnaf-paper-elevated to-carnaf-paper-sunk rounded-md">
                <div className="w-14 h-14 mx-auto mb-2 rounded-2xl bg-carnaf-teal-soft text-carnaf-teal grid place-items-center">
                  <Icon name="file" size={26} />
                </div>
                <div className="text-caption">{doc.fileMimeType.split('/').pop()?.toUpperCase()}</div>
              </div>
            )}
          </Card>

          {/* Expiry banner */}
          <ExpiryBanner doc={doc} t={t} />

          {/* Extracted fields */}
          {doc.extractedData && (
            <div>
              <div className="text-body-sm font-medium text-carnaf-ink-muted uppercase tracking-wide mb-2">
                {t('document.extracted_fields', { defaultValue: 'פרטים מהמסמך' })}
              </div>
              <Card variant="nested" padded={false}>
                <ul className="divide-y divide-carnaf-line">
                  {Object.entries(doc.extractedData)
                    .filter(([, v]) => v != null && v !== '')
                    .slice(0, 14)
                    .map(([k, v]) => (
                      <li key={k} className="px-4 py-3 flex justify-between items-baseline gap-3">
                        <span className="text-body-sm text-carnaf-ink-muted">{prettyKey(k)}</span>
                        <span className="text-body text-carnaf-ink min-w-0 truncate text-end">{renderValue(k, v)}</span>
                      </li>
                    ))}
                </ul>
              </Card>
            </div>
          )}

          {/* Source provenance */}
          <p className="text-caption text-carnaf-ink-muted leading-relaxed">
            {t('document.source', { defaultValue: 'מקור' })}: {doc.storageKey.split('/').pop()} ·{' '}
            {new Date(doc.createdAt).toLocaleDateString()}
          </p>
        </div>
      )}

      {/* Sticky CTA — Download / Replace / Delete */}
      {doc && (
        <div
          className="fixed bottom-0 inset-x-0 bg-white border-t border-carnaf-line px-5 py-4"
          style={{
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            boxShadow: '0 -1px 0 0 #E0E8E1, 0 -8px 24px -8px rgba(26, 30, 29, 0.06)',
          }}
        >
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onDownload}
              className="h-12 rounded-2xl bg-carnaf-teal text-white font-medium text-btn-sm flex items-center justify-center gap-1"
              aria-label={t('document.download_aria', { defaultValue: 'הורדת המסמך' })}
            >
              <Icon name="download" size={16} />
              <span>{t('document.download', { defaultValue: 'הורדה' })}</span>
            </button>
            <Link
              to={`/upload/registration?type=${doc.documentType}&vehicleId=${doc.vehicleId ?? ''}${kol ? '&kol=1' : ''}`}
              className="h-12 rounded-2xl border border-carnaf-line-strong text-carnaf-ink font-medium text-btn-sm flex items-center justify-center"
            >
              {t('document.replace', { defaultValue: 'החלפה' })}
            </Link>
            <Button variant="danger" size="md" holdMs={1500} onHoldConfirm={onDelete} disabled={pending}>
              <span className="text-btn-sm">{t('document.delete_hold', { defaultValue: 'מחיקה (החזיקו)' })}</span>
            </Button>
          </div>
          <p className="mt-2 text-caption text-carnaf-ink-muted text-center">
            {t('document.delete_helper', { defaultValue: '30 ימים לשחזור' })}
          </p>
        </div>
      )}
    </Layout>
  );
}

function ExpiryBanner({
  doc,
  t,
}: {
  doc: DocumentDetail;
  t: (k: string, o?: Record<string, unknown>) => string;
}): JSX.Element | null {
  if (!doc.documentExpiresAt) return null;
  const exp = new Date(doc.documentExpiresAt);
  const days = Math.ceil((exp.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  const date = exp.toLocaleDateString();
  if (days < 0) {
    // Expired: a loud solid-clay alarm (red stays reserved for danger).
    return (
      <div className="rounded-xl bg-carnaf-amber p-3.5 text-white">
        <div className="flex items-center gap-2 text-body font-bold">
          <Icon name="warning" size={18} className="stroke-white" />
          {t('wallet.expired_on', { date })}
        </div>
      </div>
    );
  }
  if (days <= 30) {
    return (
      <div className="rounded-xl bg-carnaf-amber-soft border-s-[3px] border-carnaf-amber p-3">
        <div className="flex items-center gap-2 text-body font-semibold text-carnaf-amber">
          <Icon name="warning" size={18} />
          {t('wallet.expires_in_days', { days })}
        </div>
        <div className="text-caption text-carnaf-ink-muted mt-1">{t('wallet.valid_until', { date })}</div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <StatusPill tone="success" withDot>
        {t('wallet.valid_until', { date })}
      </StatusPill>
    </div>
  );
}

function prettyKey(key: string): string {
  // human-ish label fallback when no i18n key exists
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderValue(key: string, value: unknown): React.ReactNode {
  if (value == null) return '—';
  const s = String(value);
  // Identifier-shaped values get the LTR + tabular treatment
  if (key === 'plate' || key === 'license_plate') return <Plate>{s}</Plate>;
  if (key === 'vin' || key === 'chassis') return <LTR>{s}</LTR>;
  if (key === 'agent_phone' || key === 'phone') return <Brand>{s}</Brand>;
  if (key === 'issuer' || key === 'agent_name' || key === 'make' || key === 'model') return <Brand>{s}</Brand>;
  if (key === 'policy_number') return <LTR>{s}</LTR>;
  return s;
}
